<template>
  <div class="supervisor-interactive-map" style="height: 100%;">
    <!-- Header completo - Solo mostrar si NO está en modo compacto -->
    <v-card elevation="2" class="mb-4" v-if="!enableLimitedControls">
      <v-card-title class="d-flex align-center">
        <v-icon class="mr-3" color="primary">mdi-account-supervisor</v-icon>
        <span>Vista de Mapa - Supervisor de Campo</span>
        
        <v-spacer />
        
        <!-- Información del supervisor -->
        <v-chip color="primary" variant="outlined" class="mr-2">
          <v-icon start size="small">mdi-account</v-icon>
          {{ authStore.user?.fullName }}
        </v-chip>
        
        <!-- Total de elementos -->
        <v-chip color="green" variant="outlined">
          <v-icon start size="small">mdi-map-marker</v-icon>
          {{ totalElements }} Elementos
        </v-chip>
      </v-card-title>
      
      <!-- Controles de capas - Versión completa -->
      <v-card-text>
        <v-row>
          <v-col cols="12" md="8">
            <div class="d-flex flex-wrap gap-2">
              <!-- P13-V7B (refutation): no routes affordance where the consumer did not grant the
                   layer — mirrored on the POS chip below. -->
              <v-chip
                v-if="props.showMyRoutes"
                :color="showMyRoutes ? 'primary' : 'grey'"
                :variant="showMyRoutes ? 'flat' : 'outlined'"
                @click="toggleLayer('routes')"
                clickable
              >
                <v-icon start size="small">mdi-map-marker-path</v-icon>
                Mis Rutas ({{ filteredRoutes.length }})
              </v-chip>

              <!-- P13-V7B (refutation): the layer holds the zone's OPEN incidences, not "mis" incidences. -->
              <v-chip
                :color="showMyIncidents ? 'orange' : 'grey'"
                :variant="showMyIncidents ? 'flat' : 'outlined'"
                @click="toggleLayer('incidents')"
                clickable
              >
                <v-icon start size="small">mdi-alert</v-icon>
                Incidencias abiertas de la zona ({{ filteredIncidents.length }})
              </v-chip>
              
              <!-- X2-R1: no POS affordance where the consumer did not grant the capability. -->
              <v-chip
                v-if="props.showMyPOS"
                :color="showMyPOS ? 'blue' : 'grey'"
                :variant="showMyPOS ? 'flat' : 'outlined'"
                @click="toggleLayer('pos')"
                clickable
              >
                <v-icon start size="small">mdi-point-of-sale</v-icon>
                Mis POS ({{ filteredPOSPoints.length }})
              </v-chip>
              
              <v-chip
                v-if="supervisionsCount > 0"
                :color="showSupervisions ? 'purple' : 'grey'"
                :variant="showSupervisions ? 'flat' : 'outlined'"
                @click="toggleLayer('supervisions')"
                clickable
              >
                <v-icon start size="small">mdi-eye</v-icon>
                Supervisiones ({{ supervisionsCount }})
              </v-chip>
            </div>
          </v-col>
          
          <v-col cols="12" md="4">
            <div class="d-flex justify-end">
              <v-btn
                color="primary"
                variant="outlined"
                size="small"
                @click="centerOnSupervisorData"
                class="mr-2"
              >
                <v-icon start>mdi-crosshairs-gps</v-icon>
                Centrar Datos
              </v-btn>
              
              <v-btn
                color="secondary"
                variant="outlined"
                size="small"
                @click="refreshData"
                :loading="loading"
              >
                <v-icon start>mdi-refresh</v-icon>
                Actualizar
              </v-btn>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Controles de capas compactos - Solo mostrar en modo dashboard -->
    <div v-else class="pa-3 bg-white border-b">
      <div class="d-flex align-center justify-space-between">
        <!-- Título del mapa a la izquierda -->
        <div class="d-flex align-center">
          <v-icon color="primary" size="small" class="mr-2">mdi-map</v-icon>
          <h3 class="text-subtitle-1 font-weight-medium text-primary mb-0">
            Mapa Interactivo - Zona Supervisada
          </h3>
        </div>
        
        <!-- Controles de capas en el centro -->
        <div class="d-flex flex-wrap gap-1 align-center">
          <!-- P13-V7B (refutation): no routes affordance where the consumer did not grant the layer. -->
          <v-chip
            v-if="props.showMyRoutes"
            :color="showMyRoutes ? 'primary' : 'grey'"
            :variant="showMyRoutes ? 'flat' : 'outlined'"
            @click="toggleLayer('routes')"
            clickable
            size="small"
            density="comfortable"
            elevation="1"
          >
            <v-icon start size="x-small">mdi-map-marker-path</v-icon>
            Rutas ({{ filteredRoutes.length }})
          </v-chip>
          
          <v-chip
            :color="showMyIncidents ? 'orange' : 'grey'"
            :variant="showMyIncidents ? 'flat' : 'outlined'"
            @click="toggleLayer('incidents')"
            clickable
            size="small"
            density="comfortable"
            elevation="1"
          >
            <v-icon start size="x-small">mdi-alert</v-icon>
            Incidencias ({{ filteredIncidents.length }})
          </v-chip>
          
          <!-- X2-R1: no POS affordance where the consumer did not grant the capability. -->
          <v-chip
            v-if="props.showMyPOS"
            :color="showMyPOS ? 'blue' : 'grey'"
            :variant="showMyPOS ? 'flat' : 'outlined'"
            @click="toggleLayer('pos')"
            clickable
            size="small"
            density="comfortable"
            elevation="1"
          >
            <v-icon start size="x-small">mdi-point-of-sale</v-icon>
            POS ({{ filteredPOSPoints.length }})
          </v-chip>
        </div>
        
        <!-- Controles de acción a la derecha -->
        <div class="d-flex gap-2">
          <v-btn
            color="success"
            variant="outlined"
            @click="centerOnSupervisorData"
            size="small"
            density="comfortable"
          >
            <v-icon start size="x-small">mdi-crosshairs-gps</v-icon>
            Centrar
          </v-btn>
          
          <v-btn
            color="primary"
            variant="flat"
            @click="refreshData"
            size="small"
            :loading="loading"
            density="comfortable"
          >
            <v-icon start size="x-small">mdi-refresh</v-icon>
            Actualizar
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Mapa principal -->
    <v-alert
      v-if="mapStatusMessage"
      type="info"
      variant="tonal"
      density="comfortable"
      class="ma-3"
    >
      {{ mapStatusMessage }}
    </v-alert>

    <v-card elevation="2" :class="enableLimitedControls ? 'h-100 d-flex flex-column' : ''" style="position: relative;">
      <div 
        ref="mapContainer" 
        :style="{ height: enableLimitedControls ? '100%' : mapHeight }"
        :class="enableLimitedControls ? 'flex-grow-1' : 'supervisor-map-container'"
        class="supervisor-map-container"
      ></div>
      
      <!-- Loading overlay -->
      <v-overlay
        :model-value="loading"
        contained
        class="align-center justify-center"
      >
        <v-progress-circular
          indeterminate
          size="64"
          color="primary"
        ></v-progress-circular>
        <p class="mt-2">Cargando datos del supervisor...</p>
      </v-overlay>

      <!-- Panel de información lateral (flotante dentro del mapa) -->
      <transition name="slide-fade">
        <v-card
          v-if="showInfoPanel"
          class="info-panel-floating"
          elevation="8"
        >
          <v-card-title class="d-flex align-center bg-primary info-panel-header">
            <v-icon class="mr-2">mdi-information</v-icon>
            <span class="text-truncate">Información de Elemento</span>
            <v-spacer />
            <v-btn
              icon="mdi-close"
              size="small"
              variant="text"
              color="white"
              @click="showInfoPanel = false"
            ></v-btn>
          </v-card-title>
          
          <v-card-text class="info-panel-content pa-3">
            <div v-if="selectedElement">
              <!-- Información específica según el tipo -->
              <SupervisorElementInfo
                :type="selectedElement.type"
                :data="selectedElement.data"
                :supervisor-id="supervisorId"
              />
            </div>
            <div v-else class="text-center py-4">
              <v-icon size="48" color="grey">mdi-information-outline</v-icon>
              <p class="text-grey mt-2">Selecciona un elemento en el mapa</p>
            </div>
          </v-card-text>
        </v-card>
      </transition>
    </v-card>

    <!-- 👁️ Diálogo Rico de Detalles de Incidencia -->
    <IncidentDetailsDialog
      v-model="showIncidentDialog"
      :incident="selectedIncident"
      @edit="handleEditIncident"
      @assign="handleAssignIncident"
      @comment="handleAddComment"
      @status-change="handleStatusChange"
      @close="handleCloseIncidentDialog"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
let L: typeof import('leaflet')
import { useAuthStore } from '@/stores/auth'
import { SupervisorService } from '@/services/supervisorService'
import SupervisorElementInfo from '@/components/supervisor/SupervisorElementInfo.vue'
import IncidentDetailsDialog from '@/views/incidents/components/IncidentDetailsDialog.vue'
import type { Incident, Route } from '@/types'

// Fix de iconos se aplicará tras cargar Leaflet dinámicamente

// Props
interface Props {
  supervisorId?: number
  showMyRoutes?: boolean
  showMyIncidents?: boolean
  showMyPOS?: boolean
  enableLimitedControls?: boolean
  mapHeight?: string
}

const props = withDefaults(defineProps<Props>(), {
  // P13-V7B (refutation): OFF unless a consumer grants it. `getSupervisorRoutes` has no producer (it
  // answers `[]` without a request), so a default-on layer drew "Mis Rutas (0)" on every mount.
  showMyRoutes: false,
  showMyIncidents: true,
  showMyPOS: true,
  enableLimitedControls: true,
  mapHeight: '100%'
})

// Stores y composables
const authStore = useAuthStore()

// Estado reactivo
const mapContainer = ref<HTMLElement>()
const map = ref<any>()
const loading = ref(false)
const showInfoPanel = ref(false)
const selectedElement = ref<any>(null)

// Estados para diálogos ricos
const showIncidentDialog = ref(false)
const selectedIncident = ref<any>(null)

// Capas del mapa
const routesLayer = ref<L.LayerGroup>()
const incidentsLayer = ref<L.LayerGroup>()
const posLayer = ref<L.LayerGroup>()
const supervisionsLayer = ref<L.LayerGroup>()

// Datos
const routes = ref<Route[]>([])
const incidents = ref<Incident[]>([])
const posPoints = ref<any[]>([])
const supervisions = ref<any[]>([])
const mapStatusMessage = ref('')

// Controles de visibilidad de capas
const showMyRoutes = ref(props.showMyRoutes)
const showMyIncidents = ref(props.showMyIncidents)
const showMyPOS = ref(props.showMyPOS)
const showSupervisions = ref(false)

// Computadas
const supervisorId = computed(() => props.supervisorId || authStore.user?.id)

// Para el mapa, mostramos todos los datos del supervisor sin filtrar por grupos
// ya que los datos ya vienen filtrados por el supervisor desde el backend
const filteredRoutes = computed(() => {
  console.log('🗺️ [SupervisorInteractiveMap] filteredRoutes computed:', routes.value.length)
  return routes.value
})
const filteredIncidents = computed(() => {
  console.log('🗺️ [SupervisorInteractiveMap] filteredIncidents computed:', incidents.value.length)
  return incidents.value
})
const filteredPOSPoints = computed(() => {
  console.log('🗺️ [SupervisorInteractiveMap] filteredPOSPoints computed:', posPoints.value.length)
  return posPoints.value
})

const totalElements = computed(() => 
  filteredRoutes.value.length + 
  filteredIncidents.value.length + 
  filteredPOSPoints.value.length
)

const supervisionsCount = computed(() => supervisions.value.length)

// Métodos de inicialización
const initializeMap = async () => {
  if (!mapContainer.value) {
    console.error('❌ [SupervisorInteractiveMap] Contenedor del mapa no disponible')
    return
  }

  console.log('🗺️ [SupervisorInteractiveMap] Inicializando mapa interactivo')

  // Cargar Leaflet dinámicamente y su CSS
  if (!L) {
    const leafletModule = await import('leaflet')
    L = (leafletModule as any).default || leafletModule
    await import('leaflet/dist/leaflet.css')
    // Fix Leaflet default icons
    try {
      delete (L.Icon.Default.prototype as any)._getIconUrl
      L.Icon.Default.mergeOptions({
        iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
        iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
        shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
      })
    } catch {}
  }

  // Crear mapa centrado en República Dominicana
  map.value = L.map(mapContainer.value, {
    center: [18.7357, -70.1627], // Santo Domingo
    zoom: 9,
    zoomControl: props.enableLimitedControls,
    preferCanvas: true,
    markerZoomAnimation: false,
    zoomAnimation: true,
    fadeAnimation: true
  })

  // Añadir tile layer
  L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '© OpenStreetMap contributors',
    updateWhenZooming: false,
    updateWhenIdle: true
  }).addTo(map.value)

  // Redraw tras zoom para asegurar alineación
  map.value.on('zoomend', () => {
    try {
      routesLayer.value?.eachLayer?.((l: any) => l.redraw?.())
      incidentsLayer.value?.eachLayer?.((l: any) => l.redraw?.())
      posLayer.value?.eachLayer?.((l: any) => l.redraw?.())
      supervisionsLayer.value?.eachLayer?.((l: any) => l.redraw?.())
    } catch {}
  })

  // Inicializar capas
  routesLayer.value = L.layerGroup().addTo(map.value)
  incidentsLayer.value = L.layerGroup().addTo(map.value)
  posLayer.value = L.layerGroup().addTo(map.value)
  supervisionsLayer.value = L.layerGroup().addTo(map.value)

  console.log('✅ [SupervisorInteractiveMap] Mapa inicializado correctamente')

  // Cargar datos iniciales
  loadSupervisorData()
}

// Métodos de carga de datos
const loadSupervisorData = async () => {
  if (!supervisorId.value) return

  loading.value = true
  
  try {
    console.log('🗺️ [SupervisorInteractiveMap] Cargando datos para mapa del supervisor:', supervisorId.value)
    
    // Cargar datos en paralelo usando los métodos que ya funcionan
    //
    // MGI-PILOT-W2-X2-FS-LANDING-CANONICAL-DATA-R1 — THE CAPABILITY GOVERNS ACQUISITION.
    // The POS layer reads `GET /api/field-supervisor/assigned-agencies`, a route the Field
    // Supervisor role-home perimeter contains to vendor-only. Until this repair the request was
    // issued on every mount and `showMyPOS` only decided whether the resulting markers were DRAWN
    // (see renderAllLayers below) — the delivered supervisor's landing therefore hit a contained
    // route to paint nothing. The gate reads `props.showMyPOS`, the capability the CONSUMER
    // declares, and deliberately NOT the local `showMyPOS` ref: that ref is display state which
    // `toggleLayer('pos')` flips, so gating on it would let a chip click re-authorise the request.
    // `Promise.resolve([])` keeps `posData` an array — the logging and rendering below assume one.
    //
    // P13-V7B (refutation): the routes layer follows the same rule — `props.showMyRoutes` decides
    // whether its reader is asked at all, and a withdrawn layer contributes an empty array.
    const [routesData, incidentsData, posData] = await Promise.all([
      props.showMyRoutes
        ? SupervisorService.getSupervisorRoutes(supervisorId.value)
        : Promise.resolve([] as Route[]),
      SupervisorService.getMyActiveIncidents(supervisorId.value), // Usar método que ya funciona
      props.showMyPOS
        ? SupervisorService.getSupervisorPOSPoints(supervisorId.value)
        : Promise.resolve([] as any[])
    ])

    console.log('🗺️ [SupervisorInteractiveMap] Datos cargados:', {
      routes: routesData.length,
      incidents: incidentsData.length,
      posPoints: posData.length
    })

    routes.value = routesData
    incidents.value = incidentsData
    posPoints.value = posData
    supervisions.value = []
    mapStatusMessage.value = ''

    // Renderizar capas
    renderAllLayers()
    
  } catch (error) {
    console.error('❌ [SupervisorInteractiveMap] Error loading supervisor data:', error)
    
    // En caso de error, dejar capas vacías y mostrar estado honesto
    routes.value = []
    incidents.value = []
    posPoints.value = []
    supervisions.value = []
    mapStatusMessage.value = formatMapError(error)
    
    renderAllLayers()
  } finally {
    loading.value = false
  }
}

// Métodos de renderizado de capas
const renderAllLayers = () => {
  console.log('🗺️ [SupervisorInteractiveMap] Renderizando todas las capas del mapa')
  console.log('🗺️ [SupervisorInteractiveMap] Estado de capas:', {
    showMyRoutes: showMyRoutes.value,
    showMyIncidents: showMyIncidents.value,
    showMyPOS: showMyPOS.value,
    showSupervisions: showSupervisions.value
  })
  
  if (showMyRoutes.value) renderRoutes()
  if (showMyIncidents.value) renderIncidents()
  if (showMyPOS.value) renderPOSPoints()
  if (showSupervisions.value && supervisions.value.length > 0) renderSupervisions()
  
  console.log('✅ [SupervisorInteractiveMap] Todas las capas renderizadas')
}

const renderRoutes = () => {
  if (!routesLayer.value || !map.value) return
  
  // Limpiar capa anterior
  routesLayer.value.clearLayers()
  
  console.log('🗺️ [SupervisorInteractiveMap] Renderizando rutas:', filteredRoutes.value.length)
  
  filteredRoutes.value.forEach((route) => {
    const coordinates = route.coordinates
    if (!coordinates || coordinates.length === 0) {
      mapStatusMessage.value = 'Algunas rutas no tienen coordenadas reales y no se muestran en el mapa.'
      return
    }
    
    // Verificar que las coordenadas son válidas
    if (coordinates && coordinates.length > 1) {
      // Crear polilínea de la ruta con estilos mejorados
      const polyline = L.polyline(coordinates, {
        color: route.status === 'active' ? '#2196F3' : '#757575',
        weight: 3,
        opacity: 0.9,
        smoothFactor: 1.0
      })
      
      // Popup informativo
      polyline.bindPopup(`
        <div class="route-popup">
          <h4 style="margin: 0 0 8px 0; color: #1976d2;">${route.name || `Ruta #${route.id}`}</h4>
          <p style="margin: 2px 0;"><strong>ID:</strong> ${route.id}</p>
          <p style="margin: 2px 0;"><strong>Estado:</strong> ${route.status || 'Activa'}</p>
          <p style="margin: 2px 0;"><strong>Operador:</strong> ${route.operatorId || 'Sin asignar'}</p>
          <p style="margin: 2px 0;"><strong>Progreso:</strong> ${route.progress || 0}%</p>
          ${route.estimatedTime ? `<p style="margin: 2px 0;"><strong>Tiempo estimado:</strong> ${route.estimatedTime} min</p>` : ''}
          ${route.actualTime ? `<p style="margin: 2px 0;"><strong>Tiempo real:</strong> ${route.actualTime} min</p>` : ''}
        </div>
      `)
      
      // Evento click
      polyline.on('click', () => {
        selectElement('route', route)
      })
      
      // Añadir la polilínea a la capa y verificar que se añade
  routesLayer.value?.addLayer(polyline)
      
      // Debug: verificar que la polilínea se creó correctamente
      console.log(`🗺️ Polilínea creada para ruta ${route.id}:`, {
        coordCount: coordinates.length,
        bounds: polyline.getBounds()
      })
    }
  })
  
  // Asegurar que la capa esté añadida al mapa
  if (!map.value.hasLayer(routesLayer.value)) {
    map.value.addLayer(routesLayer.value)
  }
  
  console.log('✅ [SupervisorInteractiveMap] Rutas renderizadas en el mapa:', routesLayer.value.getLayers().length, 'polilíneas')
}

const renderIncidents = () => {
  if (!incidentsLayer.value) return
  
  incidentsLayer.value.clearLayers()
  
  console.log('🗺️ [SupervisorInteractiveMap] Renderizando incidencias:', filteredIncidents.value.length)
  
  filteredIncidents.value.forEach((incident) => {
    const latitude = incident.latitude
    const longitude = incident.longitude

    if (!latitude || !longitude) {
      mapStatusMessage.value = 'Algunas incidencias no tienen coordenadas reales y no se muestran en el mapa.'
      return
    }
    
    // Mapear prioridad del backend (UPPERCASE) a valores de frontend
    let priority = 'medium'
    if (incident.priority) {
      const priorityMap: Record<string, string> = {
        'HIGH': 'high',
        'CRITICAL': 'high',
        'MEDIUM': 'medium',
        'LOW': 'low',
        // También manejar valores en minúsculas por si acaso
        'high': 'high',
        'critical': 'high',
        'medium': 'medium',
        'low': 'low'
      }
      priority = priorityMap[incident.priority] || 'medium'
    }
    
    // Mapear estado del backend (UPPERCASE) a valores de frontend
    let status = incident.status || 'PENDING'
    const statusMap: Record<string, string> = {
      'PENDING': 'Pendiente',
      'IN_PROGRESS': 'En Progreso',
      'RESOLVED': 'Resuelto',
      'CLOSED': 'Cerrado',
      // También manejar valores en minúsculas por si acaso
      'pending': 'Pendiente',
      'in_progress': 'En Progreso',
      'resolved': 'Resuelto',
      'closed': 'Cerrado'
    }
    
    const displayStatus = statusMap[status] || status
    
    const marker = L.marker([latitude, longitude], {
      icon: createIncidentIcon(priority)
    })
    
    marker.bindPopup(`
      <div class="incident-popup">
        <h4>${incident.title || `Incidencia #${incident.id}`}</h4>
        <p><strong>ID:</strong> ${incident.id}</p>
        <p><strong>Estado:</strong> ${displayStatus}</p>
        <p><strong>Prioridad:</strong> ${priority.toUpperCase()}</p>
        <p><strong>Descripción:</strong> ${incident.description || 'Sin descripción'}</p>
        <p><strong>Reportado por:</strong> ${incident.reportedBy || 'Sistema'}</p>
        ${incident.createdAt ? `<p><strong>Creado:</strong> ${new Date(incident.createdAt).toLocaleDateString()}</p>` : ''}
      </div>
    `)
    
    marker.on('click', () => {
      selectElement('incident', incident)
    })
    
    incidentsLayer.value?.addLayer(marker)
  })
  
  console.log('✅ [SupervisorInteractiveMap] Incidencias renderizadas en el mapa')
}

const renderPOSPoints = () => {
  if (!posLayer.value) return
  
  posLayer.value.clearLayers()
  
  console.log('🗺️ [SupervisorInteractiveMap] Renderizando puntos POS:', filteredPOSPoints.value.length)
  
  filteredPOSPoints.value.forEach((pos) => {
    const latitude = pos.latitude || pos.location?.lat
    const longitude = pos.longitude || pos.location?.lng
    
    if (!latitude || !longitude) {
      mapStatusMessage.value = 'Algunos puntos no tienen coordenadas reales y no se muestran en el mapa.'
      return
    }
    
    const marker = L.marker([latitude, longitude], {
      icon: createPOSIcon(pos.status || 'active')
    })
    
    marker.bindPopup(`
      <div class="pos-popup">
        <h4>${pos.name || `POS #${pos.id}`}</h4>
        <p><strong>Código:</strong> ${pos.code || pos.id}</p>
        <p><strong>Estado:</strong> ${pos.status || 'Active'}</p>
        <p><strong>Ubicación:</strong> ${pos.location || 'Sin especificar'}</p>
        <p><strong>Última actualización:</strong> ${pos.lastUpdate || 'Sin datos'}</p>
        ${pos.connectionStatus ? `<p><strong>Conexión:</strong> ${pos.connectionStatus}</p>` : ''}
      </div>
    `)
    
    marker.on('click', () => {
      selectElement('pos', pos)
    })
    
    posLayer.value?.addLayer(marker)
  })
  
  console.log('✅ [SupervisorInteractiveMap] Puntos POS renderizados en el mapa')
}

const renderSupervisions = () => {
  if (!supervisionsLayer.value) return
  
  supervisionsLayer.value.clearLayers()
  
  supervisions.value.forEach(supervision => {
    if (supervision.latitude && supervision.longitude) {
      const marker = L.marker([supervision.latitude, supervision.longitude], {
        icon: createSupervisionIcon(supervision.status)
      })
      
      marker.bindPopup(`
        <div class="supervision-popup">
          <h4>Supervisión ${supervision.id}</h4>
          <p><strong>Fecha:</strong> ${supervision.date}</p>
          <p><strong>Estado:</strong> ${supervision.status}</p>
          <p><strong>Tipo:</strong> ${supervision.type}</p>
        </div>
      `)
      
      marker.on('click', () => {
        selectElement('supervision', supervision)
      })
      
      supervisionsLayer.value?.addLayer(marker)
    }
  })
}

// Métodos de creación de iconos
const createIncidentIcon = (priority: string) => {
  const colors: Record<string, string> = {
    high: '#f44336',
    medium: '#ff9800',
    low: '#4caf50'
  }
  
  return L.divIcon({
    html: `<div style="background-color: ${colors[priority] || colors.medium}; width: 20px; height: 20px; border-radius: 50%; border: 2px solid white;"></div>`,
    iconSize: [20, 20],
    className: 'custom-incident-icon'
  })
}

const createPOSIcon = (status: string) => {
  const colors: Record<string, string> = {
    active: '#4caf50',
    inactive: '#9e9e9e',
    maintenance: '#ff9800'
  }
  
  return L.divIcon({
    html: `<div style="background-color: ${colors[status] || colors.active}; width: 16px; height: 16px; border-radius: 2px; border: 2px solid white;"></div>`,
    iconSize: [16, 16],
    className: 'custom-pos-icon'
  })
}

const createSupervisionIcon = (status: string) => {
  return L.divIcon({
    html: `<div style="background-color: #9c27b0; width: 18px; height: 18px; border-radius: 50%; border: 2px solid white; position: relative;">
             <div style="position: absolute; top: 2px; left: 2px; width: 10px; height: 10px; background-color: white; border-radius: 50%;"></div>
           </div>`,
    iconSize: [18, 18],
    className: 'custom-supervision-icon'
  })
}

// Métodos de control
const toggleLayer = (layerType: string) => {
  switch (layerType) {
    case 'routes':
      showMyRoutes.value = !showMyRoutes.value
      if (showMyRoutes.value) {
        renderRoutes()
      } else {
        routesLayer.value?.clearLayers()
      }
      break
    case 'incidents':
      showMyIncidents.value = !showMyIncidents.value
      if (showMyIncidents.value) {
        renderIncidents()
      } else {
        incidentsLayer.value?.clearLayers()
      }
      break
    case 'pos':
      showMyPOS.value = !showMyPOS.value
      if (showMyPOS.value) {
        renderPOSPoints()
      } else {
        posLayer.value?.clearLayers()
      }
      break
    case 'supervisions':
      showSupervisions.value = !showSupervisions.value
      if (showSupervisions.value) {
        renderSupervisions()
      } else {
        supervisionsLayer.value?.clearLayers()
      }
      break
  }
}

const centerOnSupervisorData = () => {
  if (!map.value) return
  
  console.log('🗺️ [SupervisorInteractiveMap] Centrando mapa en datos del supervisor')
  
  // Recopilar todas las coordenadas válidas
  const allCoordinates: [number, number][] = []
  
  // Añadir coordenadas de incidencias
  filteredIncidents.value.forEach(incident => {
    if (incident.latitude && incident.longitude) {
      allCoordinates.push([incident.latitude, incident.longitude])
    }
  })
  
  // Añadir coordenadas de POS
  filteredPOSPoints.value.forEach(pos => {
    const lat = pos.latitude || pos.location?.lat
    const lng = pos.longitude || pos.location?.lng
    if (lat && lng) {
      allCoordinates.push([lat, lng])
    }
  })
  
  // Añadir coordenadas de rutas
  filteredRoutes.value.forEach(route => {
    if (route.coordinates && route.coordinates.length > 0) {
      route.coordinates.forEach((coord: [number, number]) => {
        if (coord && coord.length >= 2) {
          allCoordinates.push([coord[0], coord[1]])
        }
      })
    }
  })
  
  console.log('🗺️ [SupervisorInteractiveMap] Coordenadas encontradas:', allCoordinates.length)
  
  if (allCoordinates.length > 0) {
    const group = new L.FeatureGroup(allCoordinates.map(coord => L.marker(coord)))
    map.value.fitBounds(group.getBounds().pad(0.1))
    console.log('✅ [SupervisorInteractiveMap] Mapa centrado en datos')
  } else {
    mapStatusMessage.value = 'No hay coordenadas reales disponibles para centrar el mapa.'
    console.log('📍 [SupervisorInteractiveMap] Sin coordenadas reales para centrar el mapa')
  }
}

const refreshData = () => {
  loadSupervisorData()
}

// Handlers para el diálogo de incidencias
const handleEditIncident = (incident: any) => {
  console.log('🗺️ [SupervisorInteractiveMap] Editando incidencia:', incident.id)
  // TODO: Implementar edición de incidencias para supervisores
  showIncidentDialog.value = false
}

const handleAssignIncident = (incident: any) => {
  console.log('🗺️ [SupervisorInteractiveMap] Asignando incidencia:', incident.id)
  // TODO: Implementar asignación de incidencias
  showIncidentDialog.value = false
}

const handleAddComment = (incident: any) => {
  console.log('🗺️ [SupervisorInteractiveMap] Agregando comentario:', incident.id)
  // TODO: Implementar comentarios en incidencias
}

const handleStatusChange = (newStatus: string) => {
  console.log('🗺️ [SupervisorInteractiveMap] Cambiando estado:', { incidentId: selectedIncident.value?.id, newStatus })
  mapStatusMessage.value = 'El cambio de estado desde el mapa está pendiente de backend scoped real.'
}

const handleCloseIncidentDialog = () => {
  showIncidentDialog.value = false
  selectedIncident.value = null
}

const selectElement = (type: string, data: any) => {
  console.log('🗺️ [SupervisorInteractiveMap] Elemento seleccionado:', { type, data })
  console.log('🗺️ [SupervisorInteractiveMap] Estructura del elemento:', Object.keys(data))
  
  if (type === 'incident') {
    // Para incidencias, usar el diálogo rico del SuperAdmin
    console.log('🗺️ [SupervisorInteractiveMap] Abriendo diálogo rico de incidencia')
    selectedIncident.value = data
    showIncidentDialog.value = true
  } else {
    // Para otros elementos, usar el panel lateral básico
    console.log('🗺️ [SupervisorInteractiveMap] Abriendo panel lateral para:', type)
    selectedElement.value = { type, data }
    showInfoPanel.value = true
  }
}

const formatMapError = (error: any) => {
  const status = error?.response?.status || error?.status
  const code = error?.response?.data?.code || error?.code

  if (status === 403 || code === 'FIELD_SUPERVISOR_SCOPE_DENIED') {
    return error?.response?.data?.message || 'Acceso denegado para los datos del mapa del supervisor.'
  }

  if (status === 501 || code === 'NOT_IMPLEMENTED_YET') {
    return error?.response?.data?.message || 'El mapa de supervisor requiere backend scoped real pendiente de implementación.'
  }

  return error?.response?.data?.message || error?.message || 'No fue posible cargar datos reales para el mapa.'
}

// Watchers
watch(() => props.supervisorId, (newId) => {
  if (newId && map.value) {
    loadSupervisorData()
  }
})

// Ciclo de vida
onMounted(() => {
  initializeMap()
})

onUnmounted(() => {
  if (map.value) {
    map.value.remove()
  }
})
</script>

<style scoped>
.supervisor-interactive-map {
  width: 100%;
}

.supervisor-map-container {
  position: relative;
  width: 100%;
}

.gap-2 > * {
  margin-right: 8px;
  margin-bottom: 8px;
}

/* Mejoras visuales para el encabezado del mapa */
.border-b {
  border-bottom: 1px solid #e0e0e0;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

/* Estilos para los chips de capas */
.v-chip {
  transition: all 0.3s ease;
}

.v-chip:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}

/* Estilos para popups personalizados */
:deep(.leaflet-popup-content) {
  margin: 8px 12px;
  font-size: 13px;
  line-height: 1.4;
}

/* Z-index para popups de Leaflet (menor que panel flotante) */
:deep(.leaflet-popup-pane) {
  z-index: 700 !important;
}

:deep(.leaflet-popup) {
  margin-bottom: 20px;
}

:deep(.route-popup h4) {
  margin: 0 0 8px 0;
  color: #1976d2;
  font-size: 14px;
  font-weight: 600;
}

:deep(.incident-popup h4) {
  margin: 0 0 8px 0;
  color: #f44336;
  font-size: 14px;
  font-weight: 600;
}

:deep(.pos-popup h4) {
  margin: 0 0 8px 0;
  color: #4caf50;
  font-size: 14px;
  font-weight: 600;
}

:deep(.supervision-popup h4) {
  margin: 0 0 8px 0;
  color: #9c27b0;
  font-size: 14px;
  font-weight: 600;
}

/* Mejoras para controles de Leaflet */
:deep(.leaflet-control-zoom a) {
  background-color: #ffffff;
  border: 1px solid #ccc;
  color: #333;
  transition: all 0.2s ease;
}

:deep(.leaflet-control-zoom a:hover) {
  background-color: #1976d2;
  color: white;
  transform: scale(1.05);
}

/* Mejorar la visualización de polilíneas */
:deep(.leaflet-interactive) {
  cursor: pointer;
  transition: all 0.2s ease;
}

:deep(.leaflet-interactive:hover) {
  stroke-opacity: 1 !important;
  stroke-width: 4 !important;
}

/* Iconos personalizados */
:deep(.custom-incident-icon),
:deep(.custom-pos-icon),
:deep(.custom-supervision-icon) {
  background: transparent !important;
  border: none !important;
}

/* Estabilidad de marcadores (evitar drift durante zoom) */
:deep(.leaflet-marker-icon) {
  will-change: top, left;
  transform: translateZ(0);
  transition: none !important;
}

/* Panel flotante de información */
.info-panel-floating {
  position: absolute;
  top: 10px;
  right: 10px;
  width: min(380px, calc(100% - 20px)); /* Se adapta al ancho disponible */
  max-width: 420px;
  max-height: 380px; /* Altura reducida */
  z-index: 1000 !important;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  overflow: hidden; /* Evita que el contenido se desborde */
}

/* Responsivo para pantallas medianas */
@media (max-width: 1400px) {
  .info-panel-floating {
    width: min(340px, calc(100% - 20px));
  }
}

/* Responsivo para pantallas pequeñas */
@media (max-width: 768px) {
  .info-panel-floating {
    top: 60px;
    right: 5px;
    left: 5px;
    width: auto;
    max-height: calc(100vh - 150px);
  }
}

/* Header del panel flotante */
.info-panel-header {
  flex-shrink: 0;
  padding: 12px 16px !important;
  font-size: 16px !important;
  font-weight: 500;
}

/* Contenido con scroll */
.info-panel-content {
  max-height: calc(380px - 56px); /* Altura total - altura del header */
  overflow-y: auto;
  overflow-x: hidden;
}

/* Scrollbar personalizada para el panel */
.info-panel-content::-webkit-scrollbar {
  width: 6px;
}

.info-panel-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.info-panel-content::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 10px;
}

.info-panel-content::-webkit-scrollbar-thumb:hover {
  background: #555;
}

/* Transiciones suaves */
.slide-fade-enter-active {
  transition: all 0.3s ease-out;
}

.slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateX(20px);
  opacity: 0;
}
</style>
